import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuthStore } from './store/authStore';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { Dashboard } from './pages/Dashboard';
import { Admin } from './pages/Admin';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { AuthCallback } from './pages/AuthCallback';
import { InstagramCallback } from './pages/InstagramCallback';
import { Instagram } from './pages/Instagram';
import { Comments } from './pages/Comments';
import { Landing } from './pages/Landing';

function ProtectedRoute({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => !!s.user);
  return isAuthenticated ? children : <Navigate to="/login" replace />;
}

function PublicOnly({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore((s) => !!s.user);
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : children;
}

// Accueil : page de présentation publique si non connectée, sinon vers le tableau de bord.
function Home() {
  const isAuthenticated = useAuthStore((s) => !!s.user);
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <Landing />;
}

function RequireRole({ role, children }: { role: string; children: ReactNode }) {
  const user = useAuthStore((s) => s.user);
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to="/dashboard" replace />;
  return children;
}

export const App = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route
          path="/login"
          element={
            <PublicOnly>
              <Login />
            </PublicOnly>
          }
        />
        <Route
          path="/register"
          element={
            <PublicOnly>
              <Register />
            </PublicOnly>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireRole role="SUPER_ADMIN">
              <Admin />
            </RequireRole>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicOnly>
              <ForgotPassword />
            </PublicOnly>
          }
        />
        {/* /reset-password est PUBLIQUE inconditionnellement : un user déjà connecté
            peut tout à fait cliquer sur le lien de reset reçu par email (cas réel
            où il a accès à son compte mais a oublié son mot de passe). On ne doit
            PAS rediriger vers le dashboard sinon il ne peut jamais changer son mdp. */}
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        {/* /auth/callback est PUBLIQUE inconditionnellement : le user arrive ici déconnecté,
            puis le composant pose la session et redirige vers /dashboard. */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* /instagram/callback est PROTÉGÉE (le user doit être connecté à Falko
            pour pouvoir voir le résultat de la connexion IG). */}
        <Route
          path="/instagram/callback"
          element={
            <ProtectedRoute>
              <InstagramCallback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/instagram"
          element={
            <ProtectedRoute>
              <Instagram />
            </ProtectedRoute>
          }
        />
        <Route
          path="/comments"
          element={
            <ProtectedRoute>
              <Comments />
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};
